import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, fieldControlClass } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AppSheet } from "./AppSheet";
import {
  clampPickerActiveIndex,
  filterPickerOptions,
  findPickerOption,
  pickerSelectables,
  stepPickerActiveIndex,
  type AppPickerOption,
} from "./picker-math";

/** Phone sheet under md; centered modal on desk — plan/35. */
const PICKER_COMPACT_MQ = "(max-width: 767px)";

export type { AppPickerOption };

/**
 * Searchable picker — trigger looks like a field; list opens as
 * full-screen sheet (phone) or centered modal (desk).
 * ↑/↓ + Enter match ⌘K (plan/35).
 */
export function AppPicker(props: {
  value: string | null;
  options: readonly AppPickerOption[];
  onChange: (value: string | null) => void;
  title: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Optional clear row (e.g. All warehouses). */
  clearLabel?: string;
  emptyLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(PICKER_COMPACT_MQ).matches
      : true,
  );

  useEffect(() => {
    const mq = window.matchMedia(PICKER_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery("");
  }, [open]);

  const selected = findPickerOption(props.options, props.value);
  const filtered = useMemo(
    () => filterPickerOptions(props.options, query),
    [props.options, query],
  );
  const selectables = useMemo(
    () => pickerSelectables(filtered, props.clearLabel),
    [filtered, props.clearLabel],
  );

  useEffect(() => {
    if (!open) return;
    if (query !== "") {
      setActiveIndex(0);
      return;
    }
    const idx = selectables.findIndex((row) => {
      if (row.kind === "clear") {
        return props.value == null || props.value === "";
      }
      return row.value === props.value;
    });
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, query, selectables, props.value]);

  useEffect(() => {
    setActiveIndex((i) => clampPickerActiveIndex(i, selectables.length));
  }, [selectables.length]);

  useEffect(() => {
    if (!open) return;
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function pick(value: string | null) {
    props.onChange(value);
    setOpen(false);
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => stepPickerActiveIndex(i, selectables.length, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => stepPickerActiveIndex(i, selectables.length, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = selectables[activeIndex];
      if (!row) return;
      if (row.kind === "clear") pick(null);
      else pick(row.value);
    }
  }

  const list = (
    <div className="space-y-3" onKeyDown={onListKeyDown}>
      <Input
        className="min-h-11"
        placeholder="Search…"
        value={query}
        autoFocus
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul className="m-0 list-none space-y-1 p-0" role="listbox">
        {props.clearLabel ? (
          <li role="option">
            <button
              type="button"
              ref={
                selectables[activeIndex]?.kind === "clear"
                  ? activeRef
                  : undefined
              }
              className={cn(
                "flex min-h-12 w-full flex-col items-start justify-center rounded-lg px-3 text-left text-sm transition",
                selectables[activeIndex]?.kind === "clear"
                  ? "bg-[var(--color-foreground)] text-[var(--color-card)]"
                  : props.value == null || props.value === ""
                    ? "bg-[color-mix(in_oklab,var(--color-foreground)_8%,transparent)] font-medium"
                    : "hover:bg-[color-mix(in_oklab,var(--color-foreground)_5%,transparent)]",
              )}
              onMouseEnter={() => {
                const i = selectables.findIndex((r) => r.kind === "clear");
                if (i >= 0) setActiveIndex(i);
              }}
              onClick={() => pick(null)}
            >
              {props.clearLabel}
            </button>
          </li>
        ) : null}
        {filtered.length === 0 ? (
          <li className="px-3 py-8 text-center text-sm text-[var(--color-muted)]">
            {props.emptyLabel ?? "No matches"}
          </li>
        ) : (
          filtered.map((o, i) => {
            const rowIndex = (props.clearLabel ? 1 : 0) + i;
            const highlighted = rowIndex === activeIndex;
            const chosen = props.value === o.value;
            return (
              <li key={o.value} role="option">
                <button
                  type="button"
                  ref={highlighted ? activeRef : undefined}
                  className={cn(
                    "flex min-h-12 w-full flex-col items-start justify-center rounded-lg px-3 py-2 text-left transition",
                    highlighted
                      ? "bg-[var(--color-foreground)] text-[var(--color-card)]"
                      : chosen
                        ? "bg-[color-mix(in_oklab,var(--color-foreground)_8%,transparent)]"
                        : "hover:bg-[color-mix(in_oklab,var(--color-foreground)_5%,transparent)]",
                  )}
                  onMouseEnter={() => setActiveIndex(rowIndex)}
                  onClick={() => pick(o.value)}
                >
                  <span className="text-sm font-medium">{o.label}</span>
                  {o.description ? (
                    <span
                      className={cn(
                        "text-xs",
                        highlighted
                          ? "opacity-70"
                          : "text-[var(--color-muted)]",
                      )}
                    >
                      {o.description}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })
        )}
      </ul>
      {selectables.length > 0 ? (
        <p className="m-0 px-1 text-[11px] text-[var(--color-muted)]">
          ↑↓ to move · Enter to choose · Esc to close
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <button
        type="button"
        id={props.id}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          fieldControlClass,
          "items-center justify-between gap-2 text-left",
          props.className,
        )}
        onClick={() => setOpen(true)}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            !selected && "text-[var(--color-muted-fg)]",
          )}
        >
          {selected?.label ??
            (props.value == null && props.clearLabel
              ? props.clearLabel
              : (props.placeholder ?? "Choose…"))}
        </span>
        <ChevronDown
          className="size-4 shrink-0 text-[var(--color-muted)]"
          aria-hidden
        />
      </button>

      {compact ? (
        <AppSheet
          open={open}
          onOpenChange={setOpen}
          title={props.title}
          description={props.description ?? `Search and pick ${props.title}`}
          trailing={
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          }
        >
          {list}
        </AppSheet>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="flex max-h-[min(80dvh,32rem)] w-[min(100%-1.5rem,24rem)] flex-col gap-0 overflow-hidden p-0">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
              <div className="min-w-0">
                <DialogTitle className="text-base">{props.title}</DialogTitle>
                <DialogDescription className="sr-only">
                  {props.description ?? `Search and pick ${props.title}`}
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-3 py-3">{list}</div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
